import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import { addProductLiabilites } from "services/apiSrv";
import { useParams } from "react-router-dom";

// ==================== Styled Components ====================
const Container = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  margin: 16px 0;
  border: 1px solid #f0f0f0;
`;

const FormTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: #001f5f;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
`;

const TextInput = styled(Field)`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24,144,255,0.2);
  }
`;

const TextArea = styled(Field).attrs({ 
  as: "textarea" 
})`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24,144,255,0.2);
  }
`;

const ButtonBar = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #1890ff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  min-width: 100px;

  &:hover:not(:disabled) { background: #40a9ff; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #595959;
  border: 1px solid #d9d9d9;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;

  &:hover { border-color: #1890ff; color: #1890ff; }
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 6px;
`;

// Add new styles for the checkbox
const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  font-weight: normal;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxInput = styled(Field)`
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin: 0;
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Liability name is required"),
  description: Yup.string().nullable(),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .positive("Amount must be positive"),
  isAdjustable: Yup.boolean().default(false)
});

// ==================== Main Component ====================
const AddLiabilityForm = forwardRef(function AddLiabilityForm(
  { items: propItems = [], onItemsChange, refetchProductDetails },
  ref
) {
  const [internalItems, setInternalItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const formikRef = useRef();
  const { id: productSystemRef } = useParams();

  const items = Array.isArray(propItems) ? propItems : internalItems;

  useImperativeHandle(ref, () => ({
    editItemById: (id) => {
      const index = items.findIndex((item) => item.id === id);
      if (index === -1) return false;

      const item = items[index];
      setEditingIndex(index);

      formikRef.current?.setValues({
        name: item.name || "",
        description: item.description != null ? String(item.description) : "",
        amount: item.amount || item.values || "",
        isAdjustable: item.isAdjustable || item.adjustable || false
      });

      return true;
    },
  }));

  const updateItems = (newItems) => {
    if (typeof onItemsChange === "function") {
      onItemsChange(newItems);
    } else {
      setInternalItems(newItems);
    }
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (!productSystemRef) {
      alert("Error: Product reference is missing.");
      setSubmitting(false);
      return;
    }

    const payload = {
      name: values.name.trim(),
      description: values.description ? String(values.description).trim() : "",
      amount: values.amount,
      isAdjustable: values.isAdjustable,

    };

    try {
      const response = await addProductLiabilites({
        productSystemRef,
        liabilities: [payload],
      });

      const saved = response?.data?.[0] || { ...payload, id: Date.now() };

      let updatedItems;
      if (editingIndex !== null) {
        updatedItems = items.map((it, i) =>
          i === editingIndex ? { ...it, ...saved } : it
        );
        setEditingIndex(null);
      } else {
        updatedItems = [...items, saved];
      }

      updateItems(updatedItems);
      resetForm({
        values: { name: "", description: "", amount: "", isAdjustable: false }
      });

      refetchProductDetails?.();
    } catch (err) {
      console.error(err);
      alert("Failed to save liability");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    setEditingIndex(null);
    resetForm({
      values: { name: "", description: "", amount: "", isAdjustable: false }
    });
  };

  return (
    <Container>
      <FormTitle>
        {editingIndex !== null ? "Edit Liability" : "Add New Liability"}
      </FormTitle>

      <Formik
        initialValues={{ name: "", description: "", amount: "", isAdjustable: false }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({ isSubmitting, resetForm, values }) => (
          <Form noValidate>
            <Row>
              <div>
                <Label htmlFor="name">Liability Name *</Label>
                <TextInput
                  name="name"
                  placeholder="e.g. Bank Loan, SARS Debt, Bond"
                  autoComplete="off"
                />
                <ErrorMessage name="name" component={ErrorText} />
              </div>
              
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <TextInput
                  name="amount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                />
                <ErrorMessage name="amount" component={ErrorText} />
              </div>

              <div>
                <Label>Adjustable</Label>
                <CheckboxContainer>
                  <CheckboxLabel>
                    <CheckboxInput
                      type="checkbox"
                      name="isAdjustable"
                    />
                    This liability amount can be adjusted
                  </CheckboxLabel>
                </CheckboxContainer>
                <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                  If checked, the amount can be modified later during the product lifecycle.
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description (optional)</Label>
                <TextArea
                  name="description"
                  rows={3}
                  placeholder="e.g. Settle by 31 Dec 2025 • Transfer with property"
                />
                <ErrorMessage name="description" component={ErrorText} />
              </div>
            </Row>

            <ButtonBar>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : editingIndex !== null
                  ? "Update"
                  : "Add Liability"}
              </PrimaryButton>

              {editingIndex !== null && (
                <SecondaryButton type="button" onClick={() => handleCancel(resetForm)}>
                  Cancel
                </SecondaryButton>
              )}
            </ButtonBar>
          </Form>
        )}
      </Formik>
    </Container>
  );
});

AddLiabilityForm.displayName = "AddLiabilityForm";
export default AddLiabilityForm;